"use client";

import { useState, useRef, useEffect } from "react";
import { X, ImagePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { useGroupStore } from "@/store/groupStore";
import { useVolunteerStore } from "@/store/volunteerStore";
import { VolunteerOpportunityFrequency } from "@/types";
import { useLanguage } from "@/context/LanguageContext";
import { ActivityLogger } from "@/lib/activityLog";
import {
  getImageUploadCopy,
  prepareImageUpload,
} from "@/lib/image-upload";

interface CreateVolunteerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResult?: (success: boolean, message: string) => void;
  preselectedGroupId?: number;
}

export default function CreateVolunteerModal({ isOpen, onClose, onResult, preselectedGroupId }: CreateVolunteerModalProps) {
  const { t, language } = useLanguage();
  const { currentUser } = useAuth();
  const { getGroupsWhereUserCanPost, groups, myMemberships, fetchGroups, fetchMyMemberships } = useGroupStore();
  const { createOpportunity, loading } = useVolunteerStore();

  // Fetch groups and the current user's memberships when modal opens
  useEffect(() => {
    if (isOpen) {
      if (groups.length === 0) fetchGroups();
      if (currentUser && myMemberships.length === 0) fetchMyMemberships(currentUser.id);
    }
  }, [isOpen, groups.length, myMemberships.length, fetchGroups, fetchMyMemberships, currentUser]);

  // Basic info
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [salary, setSalary] = useState("");
  const [frequency, setFrequency] = useState<VolunteerOpportunityFrequency>("flexible");
  const [volunteersNeeded, setVolunteersNeeded] = useState(1);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(preselectedGroupId || null);
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadCopy = getImageUploadCopy(language, 'single');

  // Update selectedGroupId when preselectedGroupId changes
  useEffect(() => {
    if (preselectedGroupId) {
      setSelectedGroupId(preselectedGroupId);
    }
  }, [preselectedGroupId]);

  // Date and Time
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [timeStart, setTimeStart] = useState("");
  const [timeEnd, setTimeEnd] = useState("");
  const [applyUrl, setApplyUrl] = useState("");

  const userGroups = currentUser ? getGroupsWhereUserCanPost(currentUser.id) : [];

  const FREQUENCY_OPTIONS = [
    { value: 'weekly' as VolunteerOpportunityFrequency, label: t.volunteers.frequencies.weekly },
    { value: 'bi-weekly' as VolunteerOpportunityFrequency, label: t.volunteers.frequencies.biWeekly },
    { value: 'monthly' as VolunteerOpportunityFrequency, label: t.volunteers.frequencies.monthly },
    { value: 'weekends' as VolunteerOpportunityFrequency, label: t.volunteers.frequencies.weekends },
    { value: 'flexible' as VolunteerOpportunityFrequency, label: t.volunteers.frequencies.flexible },
  ];

  if (!isOpen) return null;

  const handleFile = async (file: File) => {
    const { file: prepared, error } = await prepareImageUpload(file, language);
    if (error) {
      onResult?.(false, error);
      return;
    }
    setImage(prepared);
    setImageName(prepared.name);
    setPreview(URL.createObjectURL(prepared));
  };

  const resetForm = () => {
    setTitle(""); setDescription(""); setLocation(""); setSalary("");
    setFrequency("flexible");
    setVolunteersNeeded(1);
    setSelectedGroupId(null); setImage(null); setPreview(null); setImageName(null);
    setStartDate(""); setEndDate(""); setTimeStart(""); setTimeEnd("");
    setApplyUrl("");
  };

  const handleSubmit = async () => {
    if (!title.trim() || !selectedGroupId || !location.trim()) return;

    const selectedGroup = groups.find(g => g.id === selectedGroupId);
    if (!selectedGroup) return;

    // Close modal immediately
    resetForm();
    onClose();

    try {
      const { success, documentId } = await createOpportunity({
        title: title.trim(),
        organization: selectedGroup.name,
        groupId: selectedGroup.id,
        groupDocumentId: selectedGroup.documentId, // Use documentId for stable relation
        description: description.trim(),
        location: location.trim(),
        salary: salary.trim(),
        frequency,
        volunteersNeeded,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        timeStart: timeStart || undefined,
        timeEnd: timeEnd || undefined,
        applyUrl: applyUrl.trim() || undefined,
        contactName: currentUser?.username || undefined,
        contactEmail: currentUser?.email || undefined,
        image,
      });

      onResult?.(success, success ? t.modals.volunteer.jobCreatedSuccess : t.modals.volunteer.failedToCreate);
      if (success && currentUser) {
        ActivityLogger.createVolunteer(currentUser, title.trim(), documentId, {
          name: selectedGroup.name,
          documentId: selectedGroup.documentId,
        });
      }
    } catch (error) {
      console.error('src/components/ui/CreateVolunteerModal.tsx', error);
      onResult?.(false, t.modals.volunteer.errorOccurred);
    }
  };

  const inputClass = "w-full px-3 py-2 border border-border rounded-md text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary";
  const labelClass = "block text-sm font-medium text-foreground mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="create-volunteer-root" className="relative bg-background rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background z-10">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t.modals.volunteer.createJob}</h2>
            <p className="text-sm text-caption">{t.modals.volunteer.postNewOpportunity}</p>
          </div>
          <button data-testid="create-volunteer-close-btn" onClick={onClose} className="p-1 hover:bg-sidebar-hover rounded transition">
            <X size={20} className="text-foreground-muted" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className={labelClass}>{t.modals.volunteer.postingAs} *</label>
            <select
              data-testid="create-volunteer-posting-as-select"
              value={selectedGroupId ?? ""}
              onChange={(e) => setSelectedGroupId(Number(e.target.value) || null)}
              disabled={!!preselectedGroupId}
              className={`${inputClass} ${preselectedGroupId ? 'bg-background-secondary cursor-not-allowed opacity-75' : ''}`}
            >
              <option value="">{t.modals.volunteer.selectGroup}</option>
              {userGroups.map((group) => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>{t.modals.volunteer.nameOfJob} *</label>
            <input data-testid="create-volunteer-title-input" type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t.modals.volunteer.enterJobTitle} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>{t.modals.volunteer.uploadPhoto}</label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              {preview ? (
                <div>
                  <div className="relative inline-block">
                    <img src={preview} alt="Preview" className="w-32 h-32 object-contain rounded-lg mx-auto" />
                    <button onClick={() => { setImage(null); setPreview(null); setImageName(null); }} className="absolute -top-2 -right-2 bg-error text-white rounded-full p-1"><X size={16} /></button>
                  </div>
                  {imageName && <p className="text-xs text-caption mt-2 truncate">{imageName}</p>}
                </div>
              ) : (
                <>
                  <ImagePlus size={40} className="mx-auto text-caption mb-2" />
                  <button data-testid="create-volunteer-choose-file-btn" onClick={() => fileInputRef.current?.click()} className="text-sm font-medium text-foreground border border-border px-4 py-2 rounded-md hover:bg-sidebar-hover transition">{t.modals.volunteer.chooseFile}</button>
                  <p className="text-xs text-caption mt-2">{uploadCopy.uploadPrompt}</p>
                  <p className="text-xs text-caption mt-1">{uploadCopy.fileTypes}</p>
                </>
              )}
              <input
                data-testid="create-volunteer-file-input"
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    void handleFile(e.target.files[0]);
                  }
                }}
                className="hidden"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>{t.modals.volunteer.description} *</label>
            <textarea data-testid="create-volunteer-description-textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t.modals.volunteer.describeOpportunity} rows={4} className={`${inputClass} resize-none`} />
          </div>

          <div>
            <label className={labelClass}>{t.modals.volunteer.location}</label>
            <input data-testid="create-volunteer-location-input" type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t.modals.volunteer.enterLocation} className={inputClass} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="create-volunteer-date-row">
            <div>
              <label className={labelClass}>{t.modals.volunteer.startDate}</label>
              <input data-testid="create-volunteer-start-date-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{t.modals.volunteer.endDate}</label>
              <input data-testid="create-volunteer-end-date-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="create-volunteer-time-row">
            <div>
              <label className={labelClass}>{t.modals.volunteer.startTime}</label>
              <input data-testid="create-volunteer-start-time-input" type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{t.modals.volunteer.endTime}</label>
              <input data-testid="create-volunteer-end-time-input" type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="create-volunteer-salary-jobtype-row">
            <div>
              <label className={labelClass}>{t.modals.volunteer.salary} *</label>
              <input data-testid="create-volunteer-salary-input" type="text" value={salary} onChange={(e) => setSalary(e.target.value)} placeholder={t.modals.volunteer.salaryPlaceholder} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{t.modals.volunteer.jobType} *</label>
              <select data-testid="create-volunteer-job-type-select" value={frequency} onChange={(e) => setFrequency(e.target.value as VolunteerOpportunityFrequency)} className={inputClass}>
                <option value="">{t.modals.volunteer.selectJobType}</option>
                {FREQUENCY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>{t.modals.volunteer.positionsNeeded} *</label>
            <input
              data-testid="create-volunteer-positions-needed-input"
              type="number"
              min="1"
              max="999"
              value={volunteersNeeded}
              onChange={(e) => setVolunteersNeeded(Math.max(1, parseInt(e.target.value) || 1))}
              placeholder={t.modals.volunteer.numberOfVolunteers}
              className={inputClass}
            />
            <p className="text-xs text-foreground-muted mt-1">{t.modals.volunteer.howManyVolunteers}</p>
          </div>

          <div>
            <label className={labelClass}>{t.modals.volunteer.applicationLink}</label>
            <input
              data-testid="create-volunteer-apply-url-input"
              type="url"
              value={applyUrl}
              onChange={(e) => setApplyUrl(e.target.value)}
              placeholder="https://example.com/apply"
              className={inputClass}
            />
            <p className="text-xs text-foreground-muted mt-1">{t.modals.volunteer.applicationLinkHelp}</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-border sticky bottom-0 bg-background">
          <button data-testid="create-volunteer-cancel-btn" onClick={onClose} className="px-4 py-2 text-sm font-medium text-foreground hover:bg-sidebar-hover rounded-md transition">{t.common.cancel}</button>
          <button data-testid="create-volunteer-submit-btn" onClick={handleSubmit} disabled={loading || !title.trim() || !selectedGroupId || !location.trim()} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? t.modals.volunteer.creating : t.modals.volunteer.createJobBtn}
          </button>
        </div>
      </div>
    </div>
  );
}


